import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getFeatureFlags } from '../actions';
import FigoLoadingOverlay from '../components/common/FigoLoadingOverlay';

const FeatureFlagContext = createContext();

export const FeatureFlagProvider = ({ children }) => {
  const {
    flags,
    loading,
  } = useSelector(({ settings }) => settings.featureFlags, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFeatureFlags());
  }, [dispatch]);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {loading ? <FigoLoadingOverlay visible /> : null}
      {!loading && flags ? children : null}
    </FeatureFlagContext.Provider>
  );
};

const useFeatureFlag = (featureKey) => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw Error('Feature Flag Context not found');
  }

  const featureFlags = context;
  const featureFlag = useMemo(() => !!featureFlags[featureKey], []);
  return featureFlag;
};

const useSubmitRequestAccountChanges =
  () => useFeatureFlag('PetCloudSubmitForm');
const useWebViewReskin = () => useFeatureFlag('PetCloudWebViewReskin');
const useCustomerApiV1 = () => useFeatureFlag('PetCloudCustomerApiV1');
const useClaimsApiV1 = () => useFeatureFlag('PetCloudClaimsApiV1');
const useCustomerInboxDocsApiV2 = () => useFeatureFlag(
  'PetCloudCustomerApiV2InboxAndDocs',
);
const useClaimApiV2 = () => useFeatureFlag('PetCloudClaimsApiV2');
const useCustomerPets150617V1 = () => useFeatureFlag(
  'PetCloudCustomer150617ApiV1',
);
const usePolicyApiV1 = () => useFeatureFlag('PetCloudPolicyApiV1');
const useManualVet = () => useFeatureFlag('PetCloudManualVetFlow');
const useCustomerV1T176413 = () => useFeatureFlag('PetCloudCustomerV1T176413');
const useAddPetBanner = () => useFeatureFlag('PetCloudAddPetBannerT188059');
const useCancelRenewalFigo = () => useFeatureFlag('PetCloudCancelRenewalFigo');
const useRenewalScreen = () => useFeatureFlag('PetCloud194953RenewalScreen');
const useDirectCancel = () => useFeatureFlag('PetCloudT198497DirectCancel');
const useRecaptcha = () => useFeatureFlag('PetCloudRecaptcha');
const useCareCreditReimbursement =
  () => useFeatureFlag('PetCloudCareCreditReimbursement');
const useComboBoxLanguage = () => useFeatureFlag(
  'PetCloudLanguageComboT164558',
);
const useFileClaimPPUByPass = () => useFeatureFlag('PetCloudT214041PPUClaims');
const usePendingDemographics = () => useFeatureFlag(
  'PetCloudPendingDemographics',
);
const useStripeT177101 = () => useFeatureFlag('PetCloudStripeT177101');
const useOtpSMSFlow = () => useFeatureFlag('PetCloudRequireSMSVerification');
const useReimbursementVerification =
  () => useFeatureFlag('PetCloudRequireReimbursementVerification');

export {
  useAddPetBanner,
  useCancelRenewalFigo,
  useCareCreditReimbursement,
  useClaimApiV2,
  useClaimsApiV1,
  useComboBoxLanguage,
  useCustomerApiV1,
  useCustomerInboxDocsApiV2,
  useCustomerPets150617V1,
  useCustomerV1T176413,
  useDirectCancel,
  useFileClaimPPUByPass,
  useManualVet,
  useOtpSMSFlow,
  usePendingDemographics,
  usePolicyApiV1,
  useRecaptcha,
  useReimbursementVerification,
  useRenewalScreen,
  useStripeT177101,
  useSubmitRequestAccountChanges,
  useWebViewReskin,
};
